import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { financeService, type LedgerEntry, type AgingRow } from '@/services/finance.service';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { DateRangePicker } from '@/components/tasks/DateRangePicker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatINR } from '@/lib/currency';
import { formatDate } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';

const ALL = '__all__';

function typeVariant(t: LedgerEntry['reference_type']): 'default' | 'destructive' | 'success' {
  if (t === 'Payment') return 'success';
  if (t === 'Purchase') return 'destructive';
  return 'default';
}

export function LedgerPage() {
  const queryClient = useQueryClient();
  const [vendorId, setVendorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const limit = 15;

  const { data: vendorsData } = useQuery({
    queryKey: ['finance', 'vendors', { all: true }],
    queryFn: () => financeService.listVendors({ limit: 100 }),
  });

  const ledgerParams = {
    page, limit,
    vendor_id: vendorId || undefined,
    from: from || undefined,
    to: to || undefined,
  };
  const { data: ledger, isLoading, isError } = useQuery({
    queryKey: ['finance', 'ledger', ledgerParams],
    queryFn: () => financeService.getLedger(ledgerParams),
  });

  const { data: aging = [], isLoading: agingLoading } = useQuery({
    queryKey: ['finance', 'aging', vendorId || 'all'],
    queryFn: () => financeService.getAging(vendorId || undefined),
  });

  const ledgerColumns: Column<LedgerEntry>[] = [
    { key: 'date', header: 'Date', cell: (r) => <span className="whitespace-nowrap text-sm">{formatDate(r.created_at)}</span> },
    { key: 'vendor', header: 'Vendor', cell: (r) => <span className="font-medium">{r.vendor_name}</span> },
    { key: 'type', header: 'Type', cell: (r) => <Badge variant={typeVariant(r.reference_type)}>{r.reference_type}</Badge> },
    { key: 'description', header: 'Description', cell: (r) => <span className="block max-w-xs truncate text-sm text-muted-foreground">{r.description ?? '—'}</span> },
    { key: 'debit', header: 'Debit', className: 'text-right', cell: (r) => Number(r.debit) > 0 ? <span className="tabular-nums text-rose-600">{formatINR(r.debit)}</span> : <span className="text-muted-foreground">—</span> },
    { key: 'credit', header: 'Credit', className: 'text-right', cell: (r) => Number(r.credit) > 0 ? <span className="tabular-nums text-emerald-600">{formatINR(r.credit)}</span> : <span className="text-muted-foreground">—</span> },
    { key: 'balance', header: 'Balance', className: 'text-right', cell: (r) => <span className="font-mono font-semibold tabular-nums">{formatINR(r.running_balance)}</span> },
  ];

  const agingColumns: Column<AgingRow>[] = [
    { key: 'vendor', header: 'Vendor', cell: (r) => <span className="font-medium">{r.vendor_name}</span> },
    { key: 'd0_30', header: '0–30 Days', className: 'text-right', cell: (r) => <span className="tabular-nums">{formatINR(r.d0_30)}</span> },
    { key: 'd30_60', header: '30–60 Days', className: 'text-right', cell: (r) => <span className="tabular-nums">{formatINR(r.d30_60)}</span> },
    {
      key: 'd60plus', header: '60+ Days', className: 'text-right',
      cell: (r) => (
        <span className={cn('inline-flex items-center justify-end gap-1 tabular-nums', Number(r.d60plus) > 0 && 'font-semibold text-rose-600')}>
          {Number(r.d60plus) > 0 && <AlertTriangle className="h-3.5 w-3.5" />}
          {formatINR(r.d60plus)}
        </span>
      ),
    },
    { key: 'total', header: 'Total', className: 'text-right', cell: (r) => <span className="font-semibold tabular-nums">{formatINR(r.total)}</span> },
  ];

  const pagination = ledger
    ? { page: ledger.pagination.page, totalPages: ledger.pagination.totalPages, total: ledger.pagination.total, onPageChange: setPage }
    : undefined;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Vendor Ledger</h1>
        <p className="text-sm text-muted-foreground">Complete vendor-wise ledger with aging</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex flex-col gap-1.5 w-full sm:w-auto">
          <Label htmlFor="l-vendor">Vendor</Label>
          <Select
            value={vendorId || ALL}
            onValueChange={(v) => { setVendorId(v === ALL ? '' : v); setPage(1); }}
          >
            <SelectTrigger id="l-vendor" className="h-9 w-full sm:w-48"><SelectValue placeholder="All Vendors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Vendors</SelectItem>
              {(vendorsData?.data ?? []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5 w-full sm:w-auto">
          <Label>Date Range</Label>
          <DateRangePicker
            dateFrom={from}
            dateTo={to}
            onDateFromChange={(v) => { setFrom(v); setPage(1); }}
            onDateToChange={(v) => { setTo(v); setPage(1); }}
            className="w-full sm:w-64"
          />
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Ledger</h2>
        <DataTable
          columns={ledgerColumns}
          rows={ledger?.data ?? []}
          rowKey={(r) => r.id}
          loading={isLoading}
          error={isError}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: ['finance', 'ledger'] })}
          empty="No ledger entries for the current filters."
          pagination={pagination}
        />
      </div>

      <div className="space-y-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <AlertTriangle className="h-4 w-4 text-amber-500" /> Aging Report
        </h2>
        <DataTable
          columns={agingColumns}
          rows={aging}
          rowKey={(r) => r.vendor_id}
          loading={agingLoading}
          empty="No outstanding balances."
        />
      </div>
    </div>
  );
}
